import React, {useState, useEffect, useRef, useCallback} from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import {
    Container,
    Grid,
    Typography,
    makeStyles,
    Checkbox,
    Button,
    Tooltip, Chip

} from '@material-ui/core';
import getApiConnectorRoute from "../apiRoutes";
import {formatDate, getShipperIcon, getShipperSelectOptions, mapShippersId} from 'helpers';
import ServerTable from "components/ServerTable";
import ConnectorTableFilter from "../TableFilter";
import {useTranslation} from "react-i18next";
import OrderStatusesContext from "../Contexts/OrderStatusesContext";
import CodPrice from "./CodPrice";
import LoadingOverlay from "react-loading-overlay";

const useStyles = makeStyles(() => ({
    root: {
        '& table .MuiTableCell-sizeSmall': {
            padding: '6px 4px'
        }
    },
    checkbox: {
        padding: 0
    },
    button: {
        marginLeft: '10px'
    },
    setMappingButton: {
        whiteSpace: "nowrap"
    },
    exported: {
        '& td.actions svg': {
            color: 'rgba(67, 160, 71, 0.8)',
            fontWeight: '700'
        },
        '& td': {
            color: 'rgba(67, 160, 71, 0.8)',
            fontWeight: '700'
        },
        '&.MuiCardHeader-root': {
            backgroundColor: 'rgba(67, 160, 71, 1)'
        }
    },
    shipperContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
    },
    alertChip: {
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.7rem',
        height: '18px'
    },
    alertChipError: {
        backgroundColor: '#d32f2f'
    },
    alertChipWarning: {
        backgroundColor: '#ff9800'
    }
}));

const calculateDateRange = (rangeType) => {
    const now = new Date();
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    
    let fromDate, toDate;
    
    switch (rangeType) {
        case 'last_1_day':
            fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
            toDate = todayEnd;
            break;
        case 'last_2_days':
            fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0);
            toDate = todayEnd;
            break;
        case 'last_5_days':
            fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 4, 0, 0, 0, 0);
            toDate = todayEnd;
            break;
        case 'last_10_days':
            fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 9, 0, 0, 0, 0);
            toDate = todayEnd;
            break;
        case 'current_month':
            fromDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
            toDate = todayEnd;
            break;
        case 'last_month':
            fromDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
            toDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
            break;
        case 'custom':
        default:
            return null;
    }
    
    return { created_at_from: fromDate, created_at_to: toDate };
};

const ConnectorPackages = ({ AppValues, secureFetch, ...rest }) => {
    const classes = useStyles();
    const {t} = useTranslation('connector');
    const navigate = useNavigate();
    const { state } = useLocation();
    const tableFilter = useRef(null);
    const [loading, setLoading] = useState(false);
    const processor = state.processor;
    const id = state.id;
    const [list, setList] = useState([]);
    const [shipper, setShipper] = useState({ id: null, name: null, shortcut: null });
    const [selected, setSelected] = useState({});
    const [selectedAll, setSelectedAll] = useState(false);

    const [connector, setConnector] = useState({settings: {shipperMappings: []}});
    const [orderStatuses, setOrderStatuses] = useState([]);
    const [orderShippers, setOrderShippers] = useState([]);
    const [userShippers, setUserShippers] = useState([]);
    const [loadedFilter, setLoadedFilter] = useState(null); // null means not loaded yet
    const [loadedParameters, setLoadedParameters] = useState(null);
    const [filterLoaded, setFilterLoaded] = useState(false);

    useEffect(() => {
        const userShipperShortcuts = AppValues.user.user_shipper_price_lists.map(uspl => uspl.shipper.shortcut);
        setUserShippers(AppValues.shippers.filter(sh => userShipperShortcuts.includes(sh.shortcut)));
    }, [AppValues, orderShippers, connector]);
    
    const loadFilterFromAPI = useCallback(async () => {
        try {
            const response = await secureFetch(getApiConnectorRoute('filter', [id]), null, 'GET');
            
            if (response && response.filter_data && Object.keys(response.filter_data).length > 0) {
                const filterData = response.filter_data;
                
                if (filterData.date_range && filterData.date_range !== 'custom') {
                    const calculatedDates = calculateDateRange(filterData.date_range);
                    if (calculatedDates) {
                        filterData.created_at_from = calculatedDates.created_at_from;
                        filterData.created_at_to = calculatedDates.created_at_to;
                    }
                } else {
                    if (filterData.created_at_from) {
                        filterData.created_at_from = new Date(filterData.created_at_from);
                    }
                    if (filterData.created_at_to) {
                        filterData.created_at_to = new Date(filterData.created_at_to);
                    }
                }
                
                const { order, order_by, rows_per_page, page, ...filterOnly } = filterData;
                
                const parameters = {};
                if (order) parameters.order = order;
                if (order_by) parameters.order_by = order_by;
                if (rows_per_page) parameters.rows_per_page = rows_per_page;
                
                return { filter: filterOnly, parameters };
            }
            
            return null;
        } catch (error) {
            console.error('Failed to load filter:', error);
            return null;
        }
    }, [id, secureFetch]);

    const saveFilterToAPI = useCallback(async (filter) => {
        if (filter.date_range === 'custom') return;
        try {
            const filterToSave = {};

            for (const [key, value] of Object.entries(filter)) {
                if (key === 'page') continue;

                if (value !== undefined && value !== null && value !== '') {
                    if (value instanceof Date) {
                        filterToSave[key] = value.toISOString();
                    } else {
                        filterToSave[key] = value;
                    }
                }
            }
            
            const { order, order_by, rows_per_page, ...meaningfulFilter } = filterToSave;
            
            if (Object.keys(meaningfulFilter).length === 0) return;
            
            await secureFetch(
                getApiConnectorRoute('filter', [id]),
                { filter_data: filterToSave },
                'POST'
            );
        } catch (error) {
            console.error('Failed to save filter:', error);
        }
    }, [id, secureFetch]);

    const handleSelectAll = ( event ) => {
        let tmpSelected = selected;
        for ( const row of list ) {
            if(!row.in_neoship && row.shipper && !isMissingParcelshop(row)){
                if (event.target.checked) {
                    tmpSelected[row.id] = row;
                }
                else{
                    delete tmpSelected[row.id];
                }
            }
        }
        setSelected({ ...tmpSelected });
        setSelectedAll(event.target.checked);
    };

    const handleSelectOne = ( event, data ) => {
        if (event.target.checked) {
            setSelected({ ...selected, [data.id]: data });
        }
        else{
            let tmpSelected = selected;
            delete tmpSelected[data.id];
            setSelected({ ...tmpSelected });
        }
    };

    const handleResponse = (data, testdata, filter) => {
        setList(data.map(item => {
                if (Array.isArray(testdata) && testdata.length) {
                    item.in_neoship = !!(testdata.find(titem => titem.reference_number == item.package.reference_number));
                }else {
                    item.in_neoship = false;
                }
                return item;
            }).map((item, index) => {
                let packageShipper = AppValues.shippers ? AppValues.shippers.find((s) => s.id == item.shipper_id) : null;
                if (packageShipper && !shipper) {
                    setShipper(packageShipper);
                }
                return {
                    id: index,
                    order_id: item.order_id,
                    in_neoship: item.in_neoship,
                    package: item.package,
                    shipper: packageShipper,
                    alerts: Array.isArray(item.alerts) ? item.alerts : [],
                    is_parcelshop: !!item.is_parcelshop,
                    shipping_method: item.shipping_method || null,
                };
            })
            .filter(pck => filterList(pck, filter))
            .sort((a, b) => sortList(a, b, filter['order'], filter['order_by'], ['created_at'].includes(filter['order_by']) ? 'date' : 'text_numeric'))
        );
        setLoading(false);
    }

    const loadPackages = useCallback((filter) => {
        if (!filter) {
            filter = tableFilter.current;
        }
        
        if (filter.date_range && filter.date_range !== 'custom') {
            const calculatedDates = calculateDateRange(filter.date_range);
            if (calculatedDates) {
                filter = {
                    ...filter,
                    ...calculatedDates
                };
            }
        }
        
        saveFilterToAPI(filter);
        
        setLoading(true);
        var tmpFilter = {};
        for (const [key, val] of Object.entries(filter)){
            switch (key){
                case 'shipper':
                    tmpFilter['shippers'] = [val];
                    break;
                case 'statuses':
                case 'created_at_from':
                case 'created_at_to':
                    tmpFilter[key] = val;
            }
        }

        tmpFilter = Object.keys(tmpFilter).length === 0 ? null : tmpFilter;

        secureFetch(getApiConnectorRoute('packages', [id]), tmpFilter || null, tmpFilter ? "POST": "GET")
        .then(data => {
                if(data.length){
                    secureFetch(getApiConnectorRoute('test'), {reference_numbers: data.map((item, index) => item.package.reference_number)}, 'POST', false)
                        .then((testdata) => handleResponse(data, testdata, filter))
                        .catch((testdata) => handleResponse(data, testdata, filter))
                } else {
                    handleResponse(data, {}, filter);
                }
            }
        )
    }, [id, secureFetch, AppValues.user, saveFilterToAPI]);

    const loadData = (id) => {
        secureFetch(getApiConnectorRoute('read', [id]))
            .then(data => {
                setConnector(data);
            });

        secureFetch(getApiConnectorRoute('statuses', [id]))
            .then(data => setOrderStatuses(data));

        secureFetch(getApiConnectorRoute('shippers', [id]))
            .then(data => setOrderShippers(data));
    }

    const handleFormSubmit = (event) => {
        event.preventDefault();
        var ordersByShipper = {};
        for(const [orderId, order] of Object.entries(selected)){
            if(!ordersByShipper.hasOwnProperty(order.shipper.id)){
                ordersByShipper[order.shipper.id] = [];
            }
            let item = order.package;
            item.sender_name = (AppValues.user) ? AppValues.user.address.name : null;
            item.sender_company = (AppValues.user) ? AppValues.user.address.company : null;
            item.sender_street = (AppValues.user) ? AppValues.user.address.street : null;
            item.sender_city = (AppValues.user) ? AppValues.user.address.city : null;
            item.sender_zip = (AppValues.user) ? AppValues.user.address.zip : null;
            item.sender_email = (AppValues.user) ? AppValues.user.address.email : null;
            item.sender_state_code = (AppValues.user) ? AppValues.user.address.state.code : null;
            item.sender_phone = (AppValues.user) ? AppValues.user.address.phone : null;
            item.sender_house_number = (AppValues.user) ? AppValues.user.house_number : null;

            if(!item.count_of_packages) {
                item.count_of_packages = 1;
            }
            item.insurance = 0;
            item.reverse = false;
            delete item.created_at;
            if (order.shipper.shortcut.toUpperCase() !== 'PACKETA'){
                delete item.weight;
            } else {
                if(!item.weight) {
                    item.weight = 1;
                }
            }

            ordersByShipper[order.shipper.id].push({
                reference_number: item.reference_number,
                errors: [],
                order_id: order.order_id,
                package: item,
                alerts: order.alerts,
            });
        }
        navigate('/app/connector/packages/bulk', {
            state: {
                processor: processor,
                id: id,
                packages: ordersByShipper,
            }
        });
    }

    const handleButtonBackClick = (event) => {
        event.preventDefault();
        navigate('/app/connector', {
            state: {
            }
        });
    }

    const sortList = (a, b, order = 'asc', field = 'reference_number', type='text_numeric') => {
        const item1 = (order === "asc") ? a : b;
        const item2 = (order === "asc") ? b : a;
        let val1, val2;
        if(type==='date'){
            // format '2022-02-17 09:46:27'
            val1 = Date.parse(item1.package[field]);
            val2 = Date.parse(item2.package[field]);
        } else {
            val1 = item1.package[field];
            val2 = item2.package[field];
        }
        return (val1 < val2)
            ? -1
            : (val1 > val2)
                ? 1 : 0;
    }

    const filterList = (a, filter) => {
        let result = true;
        if (result && filter.hide_exported) {
            result = !a.in_neoship;
        }
        if (result && filter.reference_number) {
            const ref = (a.package.reference_number || '').toLocaleLowerCase();
            result = ref.indexOf((filter.reference_number || '').toLocaleLowerCase()) >= 0;
        }
        if (result && filter.receiver_person) {
            const tmpName = (filter.receiver_person || '').toLocaleLowerCase();
            const name = (a.package.receiver_name || '').toLocaleLowerCase();
            const company = (a.package.receiver_company || '').toLocaleLowerCase();
            result = name.indexOf(tmpName) >= 0 || company.indexOf(tmpName) >= 0;
        }
        if (result && filter.receiver_address) {
            const tmpAddress = (filter.receiver_address || '').toLocaleLowerCase();
            const street = (a.package.receiver_street || '').toLocaleLowerCase();
            const houseNumber = (a.package.receiver_house_number || '').toLocaleLowerCase();
            const city = (a.package.receiver_city || '').toLocaleLowerCase();
            const zip = (a.package.receiver_zip || '').toLocaleLowerCase();
            const stateCode = (a.package.receiver_state_code || '').toLocaleLowerCase();
            result = street.indexOf(tmpAddress) >= 0 || houseNumber.indexOf(tmpAddress) >= 0
                || city.indexOf(tmpAddress) >= 0 || zip.indexOf(tmpAddress) >= 0
                || stateCode.indexOf(tmpAddress) >= 0;
        }
        return result;
    }

    useEffect(() => {
        loadData(id);
        
        loadFilterFromAPI().then(result => {
            if (result && result.filter) {
                setLoadedFilter(result.filter);
                setLoadedParameters(result.parameters || {});
            } else {
                setLoadedFilter({});
                setLoadedParameters({});
            }
            setFilterLoaded(true);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isMissingParcelshop = (data) => {
        const hasParcelshopIdButNoAddress = data.package.parcelshop && !data.package.receiver_company && !data.package.receiver_street && !data.package.receiver_zip && !data.package.receiver_city && !data.package.receiver_state_code;
        const parcelshopRequiredButNotSelected = data.is_parcelshop && !data.package.parcelshop;
        return hasParcelshopIdButNoAddress || parcelshopRequiredButNotSelected;
    }

    const settings = {
        rows: list,
        rowsLength: list.length,
        colOrder: {
            order: loadedParameters?.order || 'desc',
            order_by: loadedParameters?.order_by || 'created_at'
        },
        refFilter: tableFilter,
        initialFilter: (loadedFilter && Object.keys(loadedFilter).length > 0) ? loadedFilter : { date_range: 'last_1_day' },
        handleTableUpdate: loadPackages,
        headCells: [
            { id: 'id', numeric: false, label: t('label.id'), search: false, hide: true, disableOrder: true },
            { id: 'checkbox', numeric: false, label: '', search: false, disableOrder: true, render: (data) => {
                    return ( !data.in_neoship && data.shipper && !isMissingParcelshop(data) &&
                        <Checkbox
                            checked={ !!selected[data.id] }
                            onChange={(event) => handleSelectOne( event, data )}
                            className={classes.checkbox}
                        />
                    );
                }, renderLabel: () => {
                    return (
                        <Tooltip title={ selectedAll ? t('label.unselect_all') : t('label.select_all') }>
                            <Checkbox
                                checked={selectedAll}
                                onChange={handleSelectAll}
                                className={classes.checkbox}
                            />
                        </Tooltip>
                    );
                } },
            { id: 'shipper', numeric: false, label: t('label.shipper'), disableOrder: true, search: true, searchType: 'select', selectChoices: mapShippersId(getShipperSelectOptions(userShippers, connector.processor), orderShippers, connector.settings.shipperMappings).map(val => {
                    return {
                        id: val.eshop.id,
                        name: val.eshop.name + (val.neo ? ' (' + val.neo.shortcut + ')' : '')
                    };
                }), render: (data) => {
                    const alerts = (data.alerts || []).filter(a => a.label);
                    const chipClass = (a) => a.severity === 'error' ? classes.alertChipError : classes.alertChipWarning;
                    if (data.shipper || alerts.length > 0) {
                        return (
                            <span className={classes.shipperContainer}>
                                {data.shipper && getShipperIcon(data.shipper.shortcut, data.shipper.name)}
                                {alerts.map((alert, i) => (
                                    <Tooltip key={i} title={alert.message}>
                                        <Chip size="small" label={alert.label} className={`${classes.alertChip} ${chipClass(alert)}`} />
                                    </Tooltip>
                                ))}
                            </span>
                        );
                    } else {
                        return (
                            <span className={classes.shipperContainer}>
                                <Button className={classes.setMappingButton} variant="contained" color="secondary" size="small" type="button" onClick={(e) => {
                                    e.preventDefault();
                                    navigate('/app/connector/new', {
                                        state: {
                                            id: id,
                                            processor: {
                                                id: processor,
                                                label: ''
                                            }
                                        }
                                    });
                                }}>{t('button.set_shipper_mapping')}</Button>
                                {data.shipping_method && <Chip size="small" label={data.shipping_method} className={`${classes.alertChip} ${classes.alertChipError}`} />}
                            </span>
                        );
                    }
                }},
            { id: 'created_at', numeric: false, label: t('label.created_at'), search: true, searchType: 'dateRange', render: (data) => formatDate(data.package.created_at, true) },
            { id: 'reference_number', numeric: false, label: t('label.order_number'), search: true, disableOrder: true, render: (data) => data.package.reference_number },
            { id: 'receiver_person', numeric: false, label: t('label.receiver_person'), search: true, disableOrder: true, render: (data) => {
                    return data.package.receiver_name + (data.package.receiver_company ? ', ' + data.package.receiver_company : '');
                } },
            { id: 'receiver_address', numeric: false, label: t('label.receiver_address'), search: true, disableOrder: true, render: (data) => {
                    if ( isMissingParcelshop(data) ) {
                        const label = data.package.parcelshop
                            ? t('label.parcelshop_not_found', {parcelshop_id: data.package.parcelshop})
                            : t('label.parcelshop_not_selected');
                        return <Chip size="small" label={label} className={`${classes.alertChip} ${classes.alertChipError}`} />
                    } else {
                        return data.package.receiver_street + (data.package.receiver_house_number ? ' ' + data.package.receiver_house_number : '') + ', ' + data.package.receiver_zip + ', ' + data.package.receiver_city + ', ' + data.package.receiver_state_code;
                    }
                } },
            { id: 'cod_price', numeric: false, label: t('label.cod_price'), search: false, render: (data) => <CodPrice data={data} /> },
            { id: 'weight', numeric: true, label: t('label.weight'), search: false, render: (data) => data.package.weight ? parseFloat(data.package.weight).toFixed(2) + ' kg' : null },
        ],
        topRightButtons: () => (
            <React.Fragment>
                <Button variant="contained" color="secondary" type="button" onClick={(event) => handleButtonBackClick(event) } className={classes.button}>{t('button.back')}</Button>
                <Button variant="contained" color="primary" type="button" disabled={ !Object.keys(selected).length } onClick={handleFormSubmit} className={classes.button}>{t('button.import')}</Button>
            </React.Fragment>
        ),
        rowClass: (data) => {
            if (data.in_neoship) {
                return classes.exported;
            }
            return '';
        },
        addFilter: {
            date_range: true,
            order_status: processor !== 'money' && orderStatuses.length > 0,
            hide_exported: true
        },
        tableFilterComponent: ConnectorTableFilter
    };

    return (
        <OrderStatusesContext.Provider value={orderStatuses}>
                    <Container maxWidth={false}>
                        <Typography variant="h1" gutterBottom >
                            {t('title.list_of_orders')}
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                {filterLoaded ? (
                                    <ServerTable 
                                        key={`connector-${id}-${filterLoaded}`}
                                        settings={settings} 
                                    />
                                ) : (
                                    <Typography variant="body1">{t('messages.loading_content')}</Typography>
                                )}
                            </Grid>
                        </Grid>
                        <LoadingOverlay
                            active={loading}
                            spinner
                            styles={{
                                wrapper: {position: 'static'}
                            }}
                            classNamePrefix='PackageListLoader_'
                            text={<Typography variant='subtitle1'>{t('messages.loading_content')}</Typography> }
                        />
                    </Container>
                </OrderStatusesContext.Provider>
    )
}

export default ConnectorPackages;